import {
  ArrowLeftRight,
  CircleCheck,
  CircleDashed,
  CreditCard,
  DoorOpen,
  Link2,
  StickyNote,
} from 'lucide-react'
import { Link, useParams, useSearchParams } from 'react-router'
import {
  Avatar,
  Chip,
  CountryCode,
  HealthRing,
  Monogram,
  Note,
  Panel,
  PlanTag,
  Tabs,
  Track,
} from '@/components/redesign/bits'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { useCrumb } from '@/app/shell'
import { useMe } from '@/features/auth/api'
import { invoiceStatusTone, label, subscriptionTone } from '@/features/billing/format'
import { statusLabel } from '@/features/tickets/format'
import type { CustomerDetail } from '@/lib/api/types'
import { formatDate, formatMoney, formatRelative, numberFmt } from '@/lib/format'
import { useNow } from '@/lib/use-now'
import { useCustomer } from './api'
import { ClosedBanner, CloseWorkspaceCard, UnusedBanner } from './closure'
import { CustomerConnectorCard as ConnectorCard } from '@/features/ai-connector/customer-connector-card'
import { CheckoutLinksCard, CrmCard, NotesCard, TimelineCard } from './crm'
import { OwnerEmailChange } from './owner-email'
import { PlanCard } from './plan-card'
import { SalesCard } from './sales-card'
import { SafetyTab } from './safety'
import { LifetimeCard } from './lifetime'
import { TopUpsCard } from './top-ups'
import { AiCreditsCard } from './ai-credits'
import { TwoFactorReset } from './two-factor-reset'

const usageLabels: Record<string, string> = {
  crawl_credits: 'Crawl credits',
  audits: 'Website audits',
  prospect_audits: 'Prospect sample audits',
  pagespeed_tests: 'PageSpeed tests',
  page_optimizations: 'WordPress page optimizations',
  pdf_reports: 'PDF reports',
  keyword_research_lookups: 'Keyword research lookups',
  keyword_metrics: 'Keyword data',
  competitor_refreshes: 'Competitor refreshes',
  authority_lookups: 'Authority scores',
  backlink_lookups: 'Backlink lookups',
  profile_lookups: 'Business Profile lookups',
  grid_points: 'Map grid points',
  ai_credits: 'AI credits',
}

const FEATURES: { key: keyof NonNullable<CustomerDetail['health']>['features']; label: string }[] = [
  { key: 'verified', label: 'Verified a website' },
  { key: 'audits', label: 'Site audits' },
  { key: 'google', label: 'Search Console or Analytics' },
  { key: 'ranks', label: 'Rank tracking' },
  { key: 'reports', label: 'Client reports' },
  { key: 'clients', label: 'Client portal' },
  { key: 'wordpress', label: 'WordPress connector' },
]

const PARTS: { key: keyof NonNullable<CustomerDetail['health']>['parts']; label: string }[] = [
  { key: 'activity', label: 'Activity' },
  { key: 'features', label: 'Feature use' },
  { key: 'billing', label: 'Billing' },
  { key: 'support', label: 'Support' },
]

type Tab = 'overview' | 'websites' | 'team' | 'billing' | 'safety' | 'support' | 'activity' | 'notes'

const COUNTRY = new Intl.DisplayNames(['en'], { type: 'region' })
const countryName = (code: string) => {
  try {
    return COUNTRY.of(code) ?? code
  } catch {
    return code
  }
}

const websiteStatus = (status: string) => {
  const words = status.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const limitText = (limit: number | 'unlimited' | null | undefined) =>
  limit === 'unlimited' ? 'no limit' : typeof limit === 'number' ? numberFmt.format(limit) : null

/**
 * One customer (redesign of 2026-10-16): who they are and how healthy, what they pay and use,
 * and tabs for their websites, team, billing, tickets, activity and notes. Opening it is
 * recorded in the staff audit log.
 */
export function CustomerPage() {
  const { customerId = '' } = useParams()
  const customer = useCustomer(customerId)
  const me = useMe()
  const [params, setParams] = useSearchParams()
  const tab = (params.get('tab') as Tab | null) ?? 'overview'
  const canManage = me.data?.role === 'owner' || me.data?.role === 'admin'
  const now = useNow()
  useCrumb(customer.data?.name ?? null)

  if (customer.isPending) return <Skeleton className="h-80 rounded-lg" />
  if (customer.isError)
    return (
      <div className="card">
        <ErrorState error={customer.error} onRetry={() => void customer.refetch()} />
      </div>
    )
  const c = customer.data
  const go = (next: Tab) => setParams(next === 'overview' ? {} : { tab: next }, { replace: true })
  const failing = c.billing.subscriptions.find((s) => s.status === 'past_due' || s.status === 'unpaid')
  const ending = c.plan_management.subscription?.ending ? c.plan_management.subscription : null
  // The subscription that is ending, by its own name: a workspace can run a bigger plan (a lifetime plan) on top of it.
  const endingName = ending ? (c.plan_management.plans.find((p) => p.key === ending.plan)?.name ?? c.plan.name) : null
  const websites = c.websites.filter((w) => !w.demo)
  const since = c.created_at ? Math.max(0, Math.round((now - Date.parse(c.created_at)) / 86400000)) : null

  return (
    <>
      <div className="ch-hero">
        <Monogram name={c.name} size="lg" />
        <div style={{ minWidth: 0 }}>
          <h1>{c.name}</h1>
          <div className="ch-meta">
            <PlanTag
              plan={c.plan.key}
              name={c.plan.name}
              yearly={c.plan_management.subscription?.interval === 'annual'}
            />
            {c.complimentary && <Chip tone="lime">Complimentary</Chip>}
            {c.internal_account && <Chip tone="warn">Internal: not in revenue</Chip>}
            {c.owner && (
              <>
                <span>·</span>
                <Avatar name={c.owner.name} size="xs" />
                <span>{c.owner.name}</span>
                <span>·</span>
                <span className="select-all">{c.owner.email}</span>
              </>
            )}
            {c.country && (
              <>
                <span>·</span>
                <CountryCode code={c.country} name={countryName(c.country)} />
                <span>{countryName(c.country)}</span>
              </>
            )}
          </div>
          {c.crm.tags.length > 0 && (
            <div className="tags" style={{ marginTop: 10 }}>
              {c.crm.tags.map((t) => (
                <Chip key={t} tone="line">
                  {t}
                </Chip>
              ))}
            </div>
          )}
        </div>
        <div className="acts" style={{ gap: 22 }}>
          {c.health && <HealthRing value={c.health.score} />}
          <div className="acts">
            <button type="button" className="btn" onClick={() => go('notes')}>
              <StickyNote aria-hidden="true" />
              Add a note
            </button>
            <button type="button" className="btn" onClick={() => go('billing')}>
              <Link2 aria-hidden="true" />
              Checkout link
            </button>
            {canManage && (
              <button type="button" className="btn primary" onClick={() => go('billing')}>
                <ArrowLeftRight aria-hidden="true" />
                Change plan
              </button>
            )}
          </div>
        </div>
      </div>

      <ClosedBanner customer={c} canManage={canManage} />
      {failing && (
        <Note icon={<CreditCard aria-hidden="true" />} title="A payment is failing">
          {failing.description}: Lemon Squeezy is retrying the card. The plan stays active until the last
          retry.
        </Note>
      )}
      {ending && !failing && (
        <Note
          icon={<DoorOpen aria-hidden="true" />}
          title={`${endingName} is set to end${ending.ends_at ? ` on ${formatDate(ending.ends_at)}` : ''}`}
        >
          You can keep the plan for them, or send a checkout link with a discount from the Billing tab.
        </Note>
      )}
      <UnusedBanner customer={c} />

      <div className="facts">
        <div>
          <small>Pays a month</small>
          <b>{formatMoney(c.sales.mrr)}</b>
          <span>
            {c.plan.key === 'free'
              ? 'Free plan'
              : c.plan.key.startsWith('lifetime') && !c.plan_management.subscription
                ? 'Lifetime plan, paid once'
                : c.plan_management.subscription?.interval === 'annual'
                  ? 'Billed yearly'
                  : c.complimentary
                    ? 'Complimentary'
                    : 'Billed monthly'}
          </span>
        </div>
        <div>
          <small>Paid so far</small>
          <b>{formatMoney(c.sales.total_net ?? c.sales.lifetime_net)}</b>
          <span>
            {since !== null ? `Customer for ${numberFmt.format(since)} ${since === 1 ? 'day' : 'days'}` : '—'}
          </span>
        </div>
        <div>
          <small>Websites</small>
          <b>{numberFmt.format(websites.length)}</b>
          <span>
            {limitText(c.limits?.websites)
              ? `of ${limitText(c.limits?.websites)} on the plan`
              : 'on the plan'}
          </span>
        </div>
        <div>
          <small>Team</small>
          <b>{numberFmt.format(c.members.length)}</b>
          <span>
            {limitText(c.limits?.team_members) ? `of ${limitText(c.limits?.team_members)} seats` : 'members'}
          </span>
        </div>
        <div>
          <small>Last active</small>
          <b>{c.last_active_at ? formatRelative(c.last_active_at) : 'Never'}</b>
          <span>
            {c.owner?.last_login_at
              ? `Owner signed in ${formatRelative(c.owner.last_login_at)}`
              : 'Owner never signed in'}
          </span>
        </div>
      </div>

      <Tabs<Tab>
        label="Customer"
        value={tab}
        onChange={go}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'websites', label: 'Websites', count: websites.length },
          { key: 'team', label: 'Team', count: c.members.length },
          { key: 'billing', label: 'Billing', count: failing ? '!' : undefined, alert: Boolean(failing) },
          {
            key: 'safety',
            label: 'Safety',
            count: c.safety?.limited ? '!' : c.safety?.duplicates.length || undefined,
            alert: Boolean(c.safety?.limited),
          },
          { key: 'support', label: 'Support', count: c.tickets.length },
          { key: 'activity', label: 'Activity' },
          { key: 'notes', label: 'Notes', count: c.crm.notes.length },
        ]}
      />

      {tab === 'overview' && <Overview c={c} canManage={canManage} />}
      {tab === 'websites' && <Websites c={c} />}
      {tab === 'team' && <Team c={c} canManage={canManage} />}
      {tab === 'billing' && <Billing c={c} canManage={canManage} />}
      {tab === 'safety' && <SafetyTab customer={c} canManage={canManage} />}
      {tab === 'support' && <Support c={c} />}
      {tab === 'activity' && <TimelineCard customer={c} />}
      {tab === 'notes' && <NotesCard customer={c} />}
    </>
  )
}

function Overview({ c, canManage }: { c: CustomerDetail; canManage: boolean }) {
  const h = c.health
  // What the plan includes this month, used or not; allowances the plan doesn't have stay out.
  const meters = Object.entries(c.usage).filter(([, u]) => u.used > 0 || u.allowance !== 0)
  return (
    <div className="split">
      <div className="sec" style={{ gap: 16 }}>
        {h && (
          <Panel
            title="Health, part by part"
            sub={`Each part scores 0 to 100. Health is their weighted average, led by activity: real work, not visits (a visit alone counts for at most 50).${h.last_worked_at ? ` Last work ${formatRelative(h.last_worked_at)}.` : ' No work yet.'}`}
          >
            <div className="hb">
              {PARTS.map((p) => {
                const value = h.parts[p.key]
                return (
                  <div key={p.key} className="hb-r">
                    <span>{p.label}</span>
                    {value === null ? (
                      <>
                        <span className="text-ink-subtle">No tickets yet, so it isn't counted</span>
                        <b>–</b>
                      </>
                    ) : (
                      <>
                        <Track ratio={value / 100} tone={value < 45 ? 'bad' : value < 70 ? 'warn' : ''} />
                        <b>{value}</b>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          </Panel>
        )}
        {h && (
          <Panel title="What they use" sub="The features that make Arva SEO part of their week">
            <div className="adopt">
              {FEATURES.map((f) => (
                <div key={f.key} className={h.features[f.key] ? 'ad' : 'ad no'}>
                  {h.features[f.key] ? (
                    <span className="ok">
                      <CircleCheck aria-hidden="true" />
                    </span>
                  ) : (
                    <CircleDashed aria-hidden="true" />
                  )}
                  <span>{f.label}</span>
                  <small>{h.features[f.key] ? 'Yes' : 'Not yet'}</small>
                </div>
              ))}
            </div>
          </Panel>
        )}
        <Panel title="This month’s usage" sub="Against what the plan includes">
          <div className="usage">
            {meters.map(([key, u]) => {
              const finite = typeof u.allowance === 'number'
              const ratio = finite && u.allowance ? u.used / (u.allowance as number) : 0
              return (
                <div key={key} className="use">
                  <div className="use-top">
                    <span>{usageLabels[key] ?? key.replace(/_/g, ' ')}</span>
                    <b>
                      {numberFmt.format(u.used)}{' '}
                      <span style={{ color: 'var(--subtle)', fontWeight: 500 }}>
                        / {u.allowance === 'unlimited' ? 'no limit' : numberFmt.format(u.allowance)}
                      </span>
                    </b>
                  </div>
                  {finite && <Track ratio={ratio} tone={ratio > 0.85 ? 'warn' : ''} />}
                  {ratio > 0.85 ? (
                    <small className="warn">Close to the limit: a good moment to offer an add-on</small>
                  ) : (
                    <small>{finite ? `${Math.round(ratio * 100)}% used` : 'No limit on this plan'}</small>
                  )}
                </div>
              )
            })}
          </div>
        </Panel>
      </div>
      <div className="sec" style={{ gap: 16 }}>
        <Panel title="How they signed up">
          <dl className="dl">
            <dt>Came from</dt>
            <dd>
              {c.owner?.signup_channel
                ? [c.owner.signup_channel, c.owner.signup_source?.utm_campaign].filter(Boolean).join(' · ')
                : 'No campaign tags'}
            </dd>
            <dt>Signed up</dt>
            <dd>
              {formatDate(c.owner?.signed_up_at ?? c.created_at)}
              {c.owner?.sign_in && `, with ${c.owner.sign_in === 'google' ? 'Google' : 'email'}`}
            </dd>
            <dt>Time zone</dt>
            <dd>{c.owner?.timezone ?? '—'}</dd>
            <dt>Product news</dt>
            <dd>
              {c.owner?.marketing_opt_in
                ? `Opted in ${formatDate(c.owner.marketing_opt_in_at ?? null)}`
                : 'Not opted in'}
            </dd>
            <dt>Email</dt>
            <dd>
              {c.owner && !c.owner.email_verified ? (
                <span style={{ color: 'var(--warn)' }}>Not verified yet</span>
              ) : (
                'Verified'
              )}
            </dd>
          </dl>
        </Panel>
        <CrmCard customer={c} />
        <Panel
          title="Latest activity"
          extra={
            <Link className="linkbtn" to="?tab=activity">
              Everything
            </Link>
          }
        >
          <div className="tl">
            {c.timeline.slice(0, 6).map((t, i) => (
              <div key={i} className="fe">
                <span
                  className={`fe-ic ${t.source === 'billing' ? 'ok' : t.source === 'support' ? '' : t.source === 'staff' ? 'warn' : 'lime'}`}
                >
                  <CircleCheck aria-hidden="true" />
                </span>
                <div className="fe-t">
                  {t.action}
                  {(t.detail || t.by) && <small>{[t.detail, t.by].filter(Boolean).join(' · ')}</small>}
                </div>
                <span className="fe-time">{formatRelative(t.at)}</span>
              </div>
            ))}
            {c.timeline.length === 0 && <p className="empty-line">Nothing yet.</p>}
          </div>
        </Panel>
        <ConnectorCard workspaceId={c.id} />
        {canManage && <CloseWorkspaceCard customer={c} />}
      </div>
    </div>
  )
}

function Websites({ c }: { c: CustomerDetail }) {
  return (
    <div className="tw">
      <table className="t" aria-label="Websites">
        <thead>
          <tr>
            <th>Website</th>
            <th>Status</th>
            <th>Audit health</th>
            <th>Keywords</th>
            <th>Google data</th>
          </tr>
        </thead>
        <tbody>
          {c.websites.map((w) => (
            <tr key={w.id}>
              <td>
                <b>{w.name}</b>
                <small>{w.domain}</small>
              </td>
              <td>
                <span className="flex flex-wrap items-center gap-1.5">
                  {w.demo && <Chip>Demo</Chip>}
                  {w.verified_at ? (
                    <Chip tone="ok">Verified</Chip>
                  ) : (
                    <Chip tone="warn">{websiteStatus(w.status)}</Chip>
                  )}
                  {(w.css_paused ?? 0) > 0 && (
                    <span title="The allowance guard: these WordPress pages' styles change on every visit, so Arva SEO stopped working out their optimized CSS for a day and reserved nothing. It lifts by itself within a day.">
                      <Chip tone="warn">
                        Optimized CSS paused on {w.css_paused} {w.css_paused === 1 ? 'page' : 'pages'}
                      </Chip>
                    </span>
                  )}
                </span>
              </td>
              <td>
                {w.health === null ? (
                  <span className="m">No audit yet</span>
                ) : (
                  <>
                    <b>{w.health}/100</b>
                    <small>{formatRelative(w.audited_at)}</small>
                  </>
                )}
              </td>
              <td className="m">
                {numberFmt.format(w.keywords)} saved · {numberFmt.format(w.tracked_keywords)} tracked
              </td>
              <td className="m">
                {w.search_console || w.analytics ? (
                  <>
                    {w.search_console && (
                      <span className="block">
                        Search Console: {w.search_console.status}
                        {w.search_console.last_synced_at &&
                          `, ${formatRelative(w.search_console.last_synced_at)}`}
                      </span>
                    )}
                    {w.analytics && (
                      <span className="block">
                        Analytics: {w.analytics.status}
                        {w.analytics.last_synced_at && `, ${formatRelative(w.analytics.last_synced_at)}`}
                      </span>
                    )}
                  </>
                ) : (
                  'Not connected'
                )}
              </td>
            </tr>
          ))}
          {c.websites.length === 0 && (
            <tr>
              <td colSpan={5} className="m">
                No websites yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function Team({ c, canManage }: { c: CustomerDetail; canManage: boolean }) {
  return (
    <div className="split">
      <div className="tw">
        <table className="t" aria-label="Team" style={{ minWidth: 680 }}>
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Last signed in</th>
              <th>Two-step verification</th>
            </tr>
          </thead>
          <tbody>
            {c.members.map((m) => (
              <tr key={`${m.email}-${m.role}`}>
                <td>
                  <div className="who">
                    <Avatar name={m.name ?? '?'} size="sm" />
                    <div>
                      <b>{m.name ?? 'Removed user'}</b>
                      <small>{m.email}</small>
                    </div>
                  </div>
                </td>
                <td>
                  {m.role}
                  {m.suspended && (
                    <small style={{ color: 'var(--warn)' }}>No access: over the plan’s seats</small>
                  )}
                </td>
                <td className="m">{m.last_login_at ? formatRelative(m.last_login_at) : 'Never'}</td>
                <td>
                  {m.two_factor ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <Chip tone="ok">On</Chip>
                      {canManage && m.user_id && m.email && (
                        <TwoFactorReset customerId={c.id} userId={m.user_id} email={m.email} />
                      )}
                    </span>
                  ) : (
                    <span className="m">Off</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Panel title="The owner’s sign-in">
        <dl className="dl">
          <dt>Signs in with</dt>
          <dd>{c.owner?.sign_in === 'google' ? 'Google' : 'Email and password'}</dd>
          <dt>Two-step verification</dt>
          <dd>{c.owner?.two_factor ? 'On' : 'Off'}</dd>
          <dt>Last active</dt>
          <dd>{c.owner?.last_active_at ? formatRelative(c.owner.last_active_at) : 'Never'}</dd>
        </dl>
        <OwnerEmailChange customer={c} />
      </Panel>
    </div>
  )
}

function Billing({ c, canManage }: { c: CustomerDetail; canManage: boolean }) {
  return (
    <div className="sec" style={{ gap: 16 }}>
      <div className="grid2">
        <PlanCard customer={c} canManage={canManage} />
        <SalesCard customer={c} canManage={canManage} />
      </div>
      <LifetimeCard customer={c} />
      <CheckoutLinksCard customer={c} />
      <TopUpsCard customer={c} canManage={canManage} />
      <AiCreditsCard customer={c} canManage={canManage} />
      <div className="tw">
        <table className="t" aria-label="Subscriptions">
          <thead>
            <tr>
              <th>Subscription</th>
              <th>Status</th>
              <th className="r">A month</th>
              <th>Next</th>
              <th>Card</th>
            </tr>
          </thead>
          <tbody>
            {c.billing.subscriptions.map((s) => (
              <tr key={s.id}>
                <td>
                  <b>{s.description}</b>
                  <small>
                    #{s.provider_id}
                    {s.customer_email && ` · ${s.customer_email}`}
                  </small>
                </td>
                <td>
                  <Chip tone={s.refunded ? '' : chipTone(subscriptionTone(s.status, s.cancelled))}>
                    {s.refunded
                      ? 'Refunded'
                      : s.cancelled && s.status !== 'cancelled' && s.status !== 'expired'
                        ? 'Ending'
                        : label(s.status)}
                  </Chip>
                  {s.test_mode && <Chip className="ml-1">Test</Chip>}
                </td>
                <td className="r tabular">{formatMoney(s.monthly_value)}</td>
                <td className="m">
                  {s.ends_at
                    ? `Ends ${formatDate(s.ends_at)}`
                    : s.renews_at
                      ? `Renews ${formatDate(s.renews_at)}`
                      : '—'}
                </td>
                <td className="m mono">{s.card ?? '—'}</td>
              </tr>
            ))}
            {c.billing.subscriptions.length === 0 && (
              <tr>
                <td colSpan={5} className="m">
                  {c.complimentary ? 'Complimentary plan: no subscription.' : 'No subscription yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {c.billing.invoices.length > 0 && (
        <div className="tw">
          <table className="t" aria-label="Payments">
            <thead>
              <tr>
                <th>Payment</th>
                <th>Status</th>
                <th className="r">Amount</th>
              </tr>
            </thead>
            <tbody>
              {c.billing.invoices.map((i) => (
                <tr key={i.id}>
                  <td>
                    <b>{formatDate(i.issued_at)}</b>
                    <small>{label(i.reason)}</small>
                  </td>
                  <td>
                    <Chip tone={chipTone(invoiceStatusTone(i.status))}>{label(i.status)}</Chip>
                  </td>
                  <td className="r tabular">
                    <b>{formatMoney(i.total_usd)}</b>
                    {i.refunded_usd > 0 && <small>{formatMoney(i.refunded_usd)} refunded</small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** The older badge tones as the redesign's chip tones. */
function chipTone(tone: string): 'ok' | 'warn' | 'bad' | 'lime' | '' {
  return tone === 'success'
    ? 'ok'
    : tone === 'warning'
      ? 'warn'
      : tone === 'danger'
        ? 'bad'
        : tone === 'accent'
          ? 'lime'
          : ''
}

function Support({ c }: { c: CustomerDetail }) {
  return (
    <div className="tw">
      <table className="t" aria-label="Support tickets" style={{ minWidth: 560 }}>
        <thead>
          <tr>
            <th>Ticket</th>
            <th>Status</th>
            <th>Last message</th>
          </tr>
        </thead>
        <tbody>
          {c.tickets.map((t) => (
            <tr key={t.id}>
              <td>
                <Link to={`/tickets/${t.id}`}>
                  <b>{t.subject}</b>
                </Link>
                <small>{t.reference}</small>
              </td>
              <td>
                <Chip tone={t.status === 'open' ? 'warn' : t.status === 'resolved' ? 'ok' : ''}>
                  {statusLabel[t.status]}
                </Chip>
              </td>
              <td className="m">{formatRelative(t.last_message_at)}</td>
            </tr>
          ))}
          {c.tickets.length === 0 && (
            <tr>
              <td colSpan={3} className="m">
                No tickets.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
